import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useApi } from "@/shared/api/use-api";

export const handsLimitQueryKey = (domain: string, partnerId: string) =>
  [`/api/v2/hands-limit/${domain}/${partnerId}`] as const;

const readHandsLimit = async (
  getHandsLimit: (
    domain: string,
    partnerId: string,
    options?: { signal?: AbortSignal }
  ) => Promise<{ data?: { limit?: number | null } }>,
  domain: string,
  partnerId: string,
  signal?: AbortSignal
) => {
  try {
    const response = await getHandsLimit(domain, partnerId, { signal });
    const limit = response.data?.limit;
    return typeof limit === "number" ? limit : null;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const useHandsLimitQuery = ({
  domain,
  partnerId,
  enabled = true,
}: {
  domain: string;
  partnerId: string;
  enabled?: boolean;
}) => {
  const { handsLimitApi } = useApi();

  return useQuery({
    queryKey: handsLimitQueryKey(domain, partnerId),
    enabled,
    queryFn: ({ signal }) =>
      readHandsLimit(
        handsLimitApi.getHandsLimit.bind(handsLimitApi),
        domain,
        partnerId,
        signal
      ),
  });
};

export const useSetHandsLimitMutation = () => {
  const { handsLimitApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      domain,
      partnerId,
      limit,
    }: {
      domain: string;
      partnerId: string;
      limit: number;
    }) => handsLimitApi.setHandsLimit(domain, partnerId, { limit }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: handsLimitQueryKey(variables.domain, variables.partnerId),
      });
    },
  });
};

export const useDeleteHandsLimitMutation = () => {
  const { handsLimitApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ domain, partnerId }: { domain: string; partnerId: string }) =>
      handsLimitApi.deleteHandsLimit(domain, partnerId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: handsLimitQueryKey(variables.domain, variables.partnerId),
      });
    },
  });
};
